import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Activity, Users, ListChecks, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/hooks/useAuth';
import StatCard from '@/components/dashboard/StatCard';
import RecentActivity from '@/components/dashboard/RecentActivity';
import ProjectSpotlight from '@/components/dashboard/ProjectSpotlight';
import UpcomingDeadlines from '@/components/dashboard/UpcomingDeadlines';

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    activeProjects: 0,
    tasksInProgress: 0,
    teamMembers: 0,
    tasksDone: 0,
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [spotlightProject, setSpotlightProject] = useState(null);
  const [upcomingDeadlines, setUpcomingDeadlinesData] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingRecentTasks, setLoadingRecentTasks] = useState(true);
  const [loadingSpotlight, setLoadingSpotlight] = useState(true);
  const [loadingDeadlines, setLoadingDeadlines] = useState(true);
  const [error, setError] = useState(null);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingStats(true);
        setLoadingRecentTasks(true);
        setLoadingSpotlight(true);
        setLoadingDeadlines(true);
        setError(null);

        const threeDaysFromNow = new Date();
        threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);

        const [
          projectsData,
          tasksInProgressData,
          tasksDoneData,
          employeesData,
          recentTasksDataRes,
          spotlightProjectDataRes,
          upcomingDeadlinesDataRes
        ] = await Promise.all([
          supabase.from('projects').select('*', { count: 'exact', head: true }),
          supabase.from('tasks')
            .select('id, custom_statuses!inner(name)', { count: 'exact' })
            .eq('custom_statuses.name', 'In Progress'),
          supabase.from('tasks')
            .select('id, custom_statuses!inner(name)', { count: 'exact' })
            .eq('custom_statuses.name', 'Done')
            .gte('updated_at', todayStart.toISOString())
            .lte('updated_at', todayEnd.toISOString()),
          supabase.from('employees').select('*', { count: 'exact', head: true }),
          supabase.from('tasks')
            .select(`
              id, 
              name, 
              updated_at, 
              projects (id, name), 
              employees (id, name)
            `)
            .order('updated_at', { ascending: false })
            .limit(3),
          supabase.from('projects')
            .select(`
              id, 
              name, 
              description, 
              tasks (
                id, 
                custom_statuses (name)
              )
            `)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase.from('tasks')
            .select(`
              id, 
              name, 
              due_date, 
              projects (id, name)
            `)
            .not('due_date', 'is', null)
            .lte('due_date', threeDaysFromNow.toISOString())
            .gte('due_date', new Date().toISOString())
            .order('due_date', { ascending: true })
            .limit(3)
        ]);
        
        if (projectsData.error) throw new Error(`Active Projects Error: ${projectsData.error.message}`);
        if (tasksInProgressData.error) throw new Error(`Tasks In Progress Error: ${tasksInProgressData.error.message}`);
        if (tasksDoneData.error) throw new Error(`Tasks Done Error: ${tasksDoneData.error.message}`);
        if (employeesData.error) throw new Error(`Team Members Error: ${employeesData.error.message}`);
        if (recentTasksDataRes.error) throw new Error(`Recent Tasks Error: ${recentTasksDataRes.error.message}`);
        if (spotlightProjectDataRes.error) throw new Error(`Spotlight Project Error: ${spotlightProjectDataRes.error.message}`);
        if (upcomingDeadlinesDataRes.error) throw new Error(`Upcoming Deadlines Error: ${upcomingDeadlinesDataRes.error.message}`);

        setStats({
          activeProjects: projectsData.count || 0,
          tasksInProgress: tasksInProgressData.count || 0,
          teamMembers: employeesData.count || 0,
          tasksDone: tasksDoneData.count || 0,
        });
        setLoadingStats(false);

        setRecentTasks(recentTasksDataRes.data || []);
        setLoadingRecentTasks(false);
        
        if (spotlightProjectDataRes.data) {
            const project = spotlightProjectDataRes.data;
            if (project.tasks && Array.isArray(project.tasks)) {
                const totalTasks = project.tasks.length;
                const doneTasks = project.tasks.filter(task => task.custom_statuses && task.custom_statuses.name === 'Done').length;
                const progress = totalTasks > 0 ? (doneTasks / totalTasks) * 100 : 0;
                setSpotlightProject({ ...project, progress });
            } else {
                 setSpotlightProject({ ...project, progress: 0, tasks: [] });
            }
        } else {
            setSpotlightProject(null);
        }
        setLoadingSpotlight(false);

        setUpcomingDeadlinesData(upcomingDeadlinesDataRes.data || []);
        setLoadingDeadlines(false);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(`Failed to load dashboard data. ${err.message}`);
        setLoadingStats(false);
        setLoadingRecentTasks(false);
        setLoadingSpotlight(false);
        setLoadingDeadlines(false);
      }
    };

    fetchData();
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-400">
        <AlertTriangle size={48} className="mb-4" />
        <p className="text-xl">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <motion.h2 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-2xl font-medium text-slate-200"
        >
          {getGreeting()}, {user?.email || 'there'}! 👋
        </motion.h2>
        <motion.h1 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
          className="text-4xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500"
        >
          Welcome to Leading Edge AI
        </motion.h1>
      </div>
      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Active Projects" value={stats.activeProjects} icon={Zap} color="text-blue-400" description="Total active initiatives" isLoading={loadingStats} />
        <StatCard title="Tasks In Progress" value={stats.tasksInProgress} icon={Activity} color="text-green-400" description="Currently being worked on" isLoading={loadingStats} />
        <StatCard title="Tasks Completed (Today)" value={stats.tasksDone} icon={ListChecks} color="text-emerald-400" description="Finished today" isLoading={loadingStats} />
        <StatCard title="Team Members" value={stats.teamMembers} icon={Users} color="text-purple-400" description="Active personnel" isLoading={loadingStats} />
      </div>

      <RecentActivity tasks={recentTasks} isLoading={loadingRecentTasks} />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProjectSpotlight project={spotlightProject} isLoading={loadingSpotlight} />
        <UpcomingDeadlines tasks={upcomingDeadlines} isLoading={loadingDeadlines} />
      </div>
    </div>
  );
};

export default DashboardPage;